"use client";

import React from "react";
import {
  PiGithubLogo,
  PiTwitterLogo,
  PiLinkedinLogo,
  PiFacebookLogo,
  PiInstagramLogo,
  PiYoutubeLogo,
  PiArrowUpRight,
  PiDribbbleLogo,
  PiDiscordLogo,
} from "react-icons/pi";
import { SocialLink, NavLink, LinkGroup } from "./types";

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  twitter: PiTwitterLogo,
  x: PiTwitterLogo,
  linkedin: PiLinkedinLogo,
  github: PiGithubLogo,
  facebook: PiFacebookLogo,
  instagram: PiInstagramLogo,
  youtube: PiYoutubeLogo,
  dribbble: PiDribbbleLogo,
  discord: PiDiscordLogo,
};

export const getSocialIcon = (platform: string, size: string = "md") => {
  const Icon = iconMap[platform.toLowerCase()] || PiArrowUpRight;
  const sizes: Record<string, string> = {
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  };
  return <Icon className={sizes[size] || sizes.md} />;
};

interface SocialLinksProps {
  links: SocialLink[];
  size?: "sm" | "md" | "lg";
  showLabels?: boolean;
  className?: string;
  accentColor?: string;
}

export const SocialLinks: React.FC<SocialLinksProps> = ({
  links,
  size = "md",
  showLabels = false,
  className = "",
  accentColor = "#3b82f6",
}) => {
  if (!links || links.length === 0) return null;

  const sizes = { sm: "h-8 w-8", md: "h-10 w-10", lg: "h-12 w-12" };
  const iconSizes = { sm: "w-4 h-4", md: "w-5 h-5", lg: "w-6 h-6" };
  const gap = size === "sm" ? "gap-3" : "gap-4";

  return (
    <div
      className={`flex items-center ${gap} ${className}`}
      role="list"
      aria-label="Social media links"
    >
      {links.map((social, index) => {
        const Icon = iconMap[social.platform.toLowerCase()] || PiArrowUpRight;
        return (
          <a
            key={index}
            href={social.url || "#"}
            className={`${sizes[size]} flex items-center justify-center rounded-full transition-all duration-200`}
            style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
            aria-label={social.label || `Visit our ${social.platform}`}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.backgroundColor = accentColor;
              e.currentTarget.style.color = "#ffffff";
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.backgroundColor = `${accentColor}20`;
              e.currentTarget.style.color = accentColor;
            }}
          >
            <Icon className={iconSizes[size]} />
          </a>
        );
      })}
    </div>
  );
};

interface NavLinksProps {
  links: NavLink[];
  className?: string;
  hoverColor?: string;
}

export const NavLinks: React.FC<NavLinksProps> = ({
  links,
  className = "",
  hoverColor = "#ffffff",
}) => {
  if (!links || links.length === 0) return null;

  return (
    <ul className={`space-y-3 ${className}`} role="list">
      {links.map((link, index) => (
        <li key={index}>
          <a
            href={link.href}
            className="text-sm transition-colors duration-150"
            style={{ color: "inherit", opacity: 0.7 }}
            onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.opacity = "1";
              e.currentTarget.style.color = hoverColor;
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLElement>) => {
              e.currentTarget.style.opacity = "0.7";
              e.currentTarget.style.color = "";
            }}
            target={link.isExternal ? "_blank" : undefined}
            rel={link.isExternal ? "noopener noreferrer" : undefined}
          >
            {link.label}
          </a>
        </li>
      ))}
    </ul>
  );
};

interface LinkGroupProps {
  group: LinkGroup;
  className?: string;
  hoverColor?: string;
}

export const LinkGroupComponent: React.FC<LinkGroupProps> = ({
  group,
  className = "",
  hoverColor = "#ffffff",
}) => {
  return (
    <div className={className}>
      <h4 className="font-semibold mb-4" style={{ color: "inherit" }}>
        {group.title}
      </h4>
      <NavLinks links={group.links} hoverColor={hoverColor} />
    </div>
  );
};

interface BrandAreaProps {
  logo?: string;
  companyName?: string | React.ReactNode;
  tagline?: string | React.ReactNode;
  description?: string | React.ReactNode;
  className?: string;
  accentColor?: string;
}

export const BrandArea: React.FC<BrandAreaProps> = ({
  logo,
  companyName,
  tagline,
  description,
  className = "",
  accentColor = "#3b82f6",
}) => {
  return (
    <div className={className}>
      {(logo || companyName) && (
        <div className="flex items-center gap-2 mb-4">
          {logo && !companyName && (
            <img src={logo} alt="Logo" className="h-8 w-auto" />
          )}
          {companyName && (
            <span
              className="text-xl font-bold tracking-wider"
              style={{ color: "inherit" }}
            >
              {companyName}
            </span>
          )}
        </div>
      )}
      {tagline && (
        <p className="text-sm font-medium mb-2" style={{ color: accentColor }}>
          {tagline}
        </p>
      )}
      {description && (
        <p
          className="text-sm max-w-sm leading-relaxed"
          style={{ color: "inherit", opacity: 0.7 }}
        >
          {description}
        </p>
      )}
    </div>
  );
};

interface NewsletterProps {
  placeholder?: string;
  buttonText?: string;
  onSubscribe?: (email: string) => void;
  className?: string;
  accentColor?: string;
  textColor?: string;
}

export const Newsletter: React.FC<NewsletterProps> = ({
  placeholder = "Enter your email",
  buttonText = "Subscribe",
  onSubscribe,
  className = "",
  accentColor = "#3b82f6",
  textColor = "#ffffff",
}) => {
  return (
    <div className={className}>
      <h4 className="font-semibold mb-3" style={{ color: textColor }}>
        Stay Updated
      </h4>
      <p className="text-sm mb-4" style={{ color: "rgba(255,255,255,0.6)" }}>
        Subscribe to our newsletter for updates.
      </p>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <input
          type="email"
          placeholder={placeholder}
          className="flex-1 px-4 py-2 rounded-lg text-sm focus:outline-none"
          style={{
            backgroundColor: "rgba(255,255,255,0.1)",
            border: "1px solid rgba(255,255,255,0.2)",
            color: textColor,
          }}
          required
        />
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium rounded-lg"
          style={{ backgroundColor: accentColor, color: "#ffffff" }}
        >
          {buttonText}
        </button>
      </form>
    </div>
  );
};

interface DividerProps {
  className?: string;
  color?: string;
}

export const Divider: React.FC<DividerProps> = ({
  className = "",
  color = "rgba(255,255,255,0.1)",
}) => {
  return (
    <div
      className={`border-t ${className}`}
      style={{ borderColor: color }}
      role="separator"
    />
  );
};

interface CopyrightProps {
  text?: string | React.ReactNode;
  className?: string;
  color?: string;
}

export const Copyright: React.FC<CopyrightProps> = ({
  text = "© 2026 All rights reserved.",
  className = "",
  color = "rgba(255,255,255,0.4)",
}) => {
  return (
    <p className={`text-sm ${className}`} style={{ color }}>
      {text}
    </p>
  );
};

const BaseFooterComponents = {
  SocialLinks,
  NavLinks,
  LinkGroup: LinkGroupComponent,
  BrandArea,
  Newsletter,
  Divider,
  Copyright,
  getSocialIcon,
};

export default BaseFooterComponents;
